/**
 * Module valuation-cost defines cost valuation parameters and results for NHK, BKI, and
 * revaluation methods, including construction indices and structure costs.
 *
 * Market-value and mortgage-lending calculations share this module.
 */
import { z } from 'zod';
import { PercentageSchema } from './measurements';
import { MonetaryValueSchema } from './money';
import { RoundingStrategySchema } from './valuation-primitives';
export const ConstructionIndexItemSchema = z.object({
    year: z.number(),
    quarter: z.string(),
    value: z.number(),
});
const ConstructionIndexSchema = z.object({
    current: z.union([ConstructionIndexItemSchema, z.number()]).optional(),
    base: z.number().optional(),
    normalized: z.number().optional(),
    has_custom_base: z.boolean(),
    has_custom_normalized: z.boolean(),
});
const ConstructionCostScopeSchema = z.enum(['gross_floor_area', 'usage_area', 'total']);
const RevaluationParameterSchema = z.object({
    cost_scope: ConstructionCostScopeSchema,
    previous_cost_value: MonetaryValueSchema,
});
const AverageApartmentSizeSchema = z.enum(['small', 'medium', 'large']);
const ApartmentLayoutSchema = z.enum(['single', 'double', 'triple', 'quadruple']);
const CostValuationStructureParameterSchema = z.object({
    construction_index: ConstructionIndexSchema,
    security_discount: z.number(),
    average_apartment_size: AverageApartmentSizeSchema,
    layout: ApartmentLayoutSchema,
    quality_level: z.number(),
    average_apartment_size_factor: z.number().optional(),
    layout_factor: z.number().optional(),
    additional_construction_cost_factor: z.number().optional(),
    extra_construction_cost: MonetaryValueSchema.optional(),
    construction_cost_adjustment: z.number(),
    revaluation_parameters: RevaluationParameterSchema.optional(),
    structure_value_rounding: RoundingStrategySchema.optional(),
});
const ConstructionCostTypeSchema = z.enum(['bki', 'nhk', 'revaluation']);
const CostValuationParameterSetSchema = z.object({
    construction_cost_type: ConstructionCostTypeSchema,
    regional_construction_cost_factor: z.number(),
    exterior_construction_cost_factor: z.number(),
    market_adjustment_factor: z.number(),
    structure_parameters: CostValuationStructureParameterSchema.array(),
    user_defined_value: MonetaryValueSchema.optional(),
    sustainability_discount: PercentageSchema.optional(),
});
export const CostValuationParametersSchema = z.object({
    market: CostValuationParameterSetSchema,
    mortgage: CostValuationParameterSetSchema,
});
const StructureConstructionCostBaseSchemaShape = {
    base_construction_cost_per_sqm: MonetaryValueSchema,
    depreciation_factor: z.number(),
    depreciation_value: MonetaryValueSchema,
    depreciated_construction_value: MonetaryValueSchema,
    exterior_construction_value: MonetaryValueSchema,
    combined_construction_value: MonetaryValueSchema,
    structure_value_rounding: RoundingStrategySchema,
};
const NhkMarketStructureCostSchema = z.object({
    ...StructureConstructionCostBaseSchemaShape,
    structure_cost_type: z.literal('nhk'),
    manufacturing_cost_id: z.string(),
    model_adjustment_factor: z.number(),
    model_adjusted_construction_cost_per_sqm: MonetaryValueSchema,
    extra_construction_cost: MonetaryValueSchema,
    additional_construction_cost_per_sqm: MonetaryValueSchema,
    adjusted_construction_cost_per_sqm: MonetaryValueSchema,
    construction_cost_per_sqm: MonetaryValueSchema,
    normal_construction_cost_per_sqm: MonetaryValueSchema,
    normal_construction_cost: MonetaryValueSchema,
    total_construction_cost: MonetaryValueSchema,
    indexed_total_construction_cost: MonetaryValueSchema,
    regional_indexed_total_construction_cost: MonetaryValueSchema,
    combined_construction_value_rounded: MonetaryValueSchema,
});
const BkiMarketStructureCostSchema = z.object({
    ...StructureConstructionCostBaseSchemaShape,
    structure_cost_type: z.literal('bki'),
    base_construction_cost_per_unit: MonetaryValueSchema,
    extra_construction_cost: MonetaryValueSchema,
    additional_construction_cost_per_sqm: MonetaryValueSchema,
    additional_construction_cost_per_unit: MonetaryValueSchema,
    construction_cost_per_sqm: MonetaryValueSchema,
    construction_cost_per_unit: MonetaryValueSchema,
    total_construction_without_extra_cost: MonetaryValueSchema,
    total_construction_cost: MonetaryValueSchema,
    indexed_total_construction_cost: MonetaryValueSchema,
    regional_indexed_total_construction_cost: MonetaryValueSchema,
    combined_construction_value_rounded: MonetaryValueSchema,
});
const RevaluationStructureCostSchema = z.object({
    ...StructureConstructionCostBaseSchemaShape,
    structure_cost_type: z.literal('revaluation'),
    base_construction_cost_per_unit: MonetaryValueSchema,
    indexed_construction_cost_per_sqm: MonetaryValueSchema,
    indexed_construction_cost_per_unit: MonetaryValueSchema,
    extra_construction_cost: MonetaryValueSchema,
    additional_construction_cost_per_sqm: MonetaryValueSchema,
    additional_construction_cost_per_unit: MonetaryValueSchema,
    construction_cost_per_sqm: MonetaryValueSchema,
    construction_cost_per_unit: MonetaryValueSchema,
    total_construction_without_extra_cost: MonetaryValueSchema,
    total_construction_cost: MonetaryValueSchema,
    indexed_total_construction_cost: MonetaryValueSchema,
    regional_indexed_total_construction_cost: MonetaryValueSchema,
    combined_construction_value_rounded: MonetaryValueSchema,
});
const NhkMortgageStructureCostSchema = z.object({
    ...StructureConstructionCostBaseSchemaShape,
    structure_cost_type: z.literal('nhk'),
    manufacturing_cost_id: z.string(),
    model_adjustment_factor: z.number(),
    model_adjusted_construction_cost_per_sqm: MonetaryValueSchema,
    extra_construction_cost: MonetaryValueSchema,
    model_adjusted_base_construction_cost_per_sqm: MonetaryValueSchema,
    construction_cost_adjustment: MonetaryValueSchema,
    construction_cost_adjustment_per_sqm: MonetaryValueSchema,
    adjusted_construction_cost_per_sqm: MonetaryValueSchema,
    base_extra_construction_cost: MonetaryValueSchema,
    base_extra_construction_cost_per_sqm: MonetaryValueSchema,
    total_construction_cost_per_sqm: MonetaryValueSchema,
    indexed_total_construction_cost_per_sqm: MonetaryValueSchema,
    regional_indexed_total_construction_cost_per_sqm: MonetaryValueSchema,
    regional_indexed_total_construction_cost: MonetaryValueSchema,
    security_discount: MonetaryValueSchema,
    security_discount_factor: z.number(),
});
const BkiMortgageStructureCostSchema = z.object({
    ...StructureConstructionCostBaseSchemaShape,
    structure_cost_type: z.literal('bki'),
    base_construction_cost_per_unit: MonetaryValueSchema,
    extra_construction_cost: MonetaryValueSchema,
    base_extra_construction_cost: MonetaryValueSchema,
    base_extra_construction_cost_per_sqm: MonetaryValueSchema,
    base_extra_construction_cost_per_unit: MonetaryValueSchema,
    total_construction_cost_per_sqm: MonetaryValueSchema,
    total_construction_cost_per_unit: MonetaryValueSchema,
    indexed_total_construction_cost_per_sqm: MonetaryValueSchema,
    indexed_total_construction_cost_per_unit: MonetaryValueSchema,
    regional_indexed_total_construction_cost_per_sqm: MonetaryValueSchema,
    regional_indexed_total_construction_cost_per_unit: MonetaryValueSchema,
    regional_indexed_total_construction_cost: MonetaryValueSchema,
    security_discount: MonetaryValueSchema,
    security_discount_factor: z.number(),
    additional_construction_cost: MonetaryValueSchema,
});
const BaseCostValuationSchemaShape = {
    total_construction_cost: MonetaryValueSchema,
    total_additional_construction_cost: MonetaryValueSchema,
    exterior_construction_value: MonetaryValueSchema,
    combined_construction_value: MonetaryValueSchema,
    preliminary_construction_value: MonetaryValueSchema,
    market_adjustment: MonetaryValueSchema,
    market_adjusted_preliminary_construction_value: MonetaryValueSchema,
    asset_value_non_rounded: MonetaryValueSchema,
    asset_value_rounded: MonetaryValueSchema,
    land_value: MonetaryValueSchema,
    rounding_strategy: RoundingStrategySchema,
    user_defined_value: MonetaryValueSchema.optional(),
    special_property_features_value: MonetaryValueSchema,
};
const MarketStructureConstructionCostSchema = z.union([NhkMarketStructureCostSchema, BkiMarketStructureCostSchema, RevaluationStructureCostSchema]);
const MortgageStructureConstructionCostSchema = z.union([NhkMortgageStructureCostSchema, BkiMortgageStructureCostSchema]);
const MarketCostValuationSchema = z.object({
    ...BaseCostValuationSchemaShape,
    structure_construction_costs: MarketStructureConstructionCostSchema.array(),
    combined_construction_value_rounded: MonetaryValueSchema,
});
const MortgageCostValuationSchema = z.object({
    ...BaseCostValuationSchemaShape,
    structure_construction_costs: MortgageStructureConstructionCostSchema.array(),
    security_discount: MonetaryValueSchema,
    sustainability_discount: MonetaryValueSchema,
    combined_with_security_discount_applied: MonetaryValueSchema,
    combined_with_security_discount_applied_and_total_additional: MonetaryValueSchema,
});
export const CombinedCostValuationSchema = z.object({
    market: MarketCostValuationSchema,
    mortgage: MortgageCostValuationSchema,
});
